import React, { useEffect, useState } from 'react';
import { FeatureCard } from '../components/feature-card/feature-card';
import { PaywallModal } from '../components/paywall/paywall-modal';
import { DSMMatrix } from '../components/dsm-matrix/dsm-matrix';
import styles from './freemium-dashboard.layout.module.css';
import { useAppActions, useAppState } from '../../../overmind';
import { PagePath } from '../../../navigation/navigation.enums';
import { WidgetCard } from '../../analytics/analytics-filters/widget-card';
import { ActionCard, Button } from '@keplerco/core';
import { KeplerState } from '../../../models/kepler-state';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';
import { CompletionStatus, OrganizationLevelType } from '../../../enums';
import { SortByOption } from '../../../enums/sort-by-option';
import { MAX_PAGE_SIZE } from '../../../library/consts/consts';
import { EntitiesCardWidget } from '../../../components/cards/entities-card.widget';
import { InsightsWidget } from '../freemium-onboarding/insights/insights-widget';

/**
 * Dashboard page for authenticated users
 * This is the main landing page after authentication
 */
export function FreemiumDashboardLayout(): JSX.Element {
  // Using any type for state for now until we define proper types
  const actions = useAppActions();
  const { user, companyVariables, freemiumAccount } = useAppState<KeplerState>();
  const keplerNavigate = useKeplerNavigate();

  // State for paywall modal
  const [isPaywallModalOpen, setIsPaywallModalOpen] = useState(false);
  const [pendingLearnerAssessmentsCount, setPendingLearnerAssessmentsCount] = useState<number>(0);
  const [isAssessmentLoading, setIsAssessmentLoading] = useState<boolean>(true);
  const [pollingCount, setPollingCount] = useState<number>(0);

  // Data for the dashboard
  const rolesCount = freemiumAccount?.roleCount || 0;
  const skillsCount = freemiumAccount?.skillCount || 0;
  const peopleCount = freemiumAccount?.userCount || 0;
  const maxFreemiumUsers = 5;
  const hasCompletedAssessment = freemiumAccount?.progress?.hasCompletedPeerAssessment || false;

  // Poll for assessment data every 10 seconds if assessment is not ready yet
  useEffect(() => {
    async function getData() {
      setIsAssessmentLoading(true);
      try {
        const result = await actions.getLearnerAssessments({
          organizationLevel: OrganizationLevelType.Learner,
          companySlug: companyVariables.slug,
          departmentSlug: undefined,
          teamSlug: undefined,
          learnerSlug: user?.learnerSlug,
          search: undefined,
          sortBy: SortByOption.MostRecentAssigned,
          pageSize: MAX_PAGE_SIZE,
          page: 1,
          isArchived: false,
        });

        if (!!result) {
          const pendingCount = result.items.filter(value =>
            value.isActive && value.completionStatus !== CompletionStatus.Completed
          ).length;
          setPendingLearnerAssessmentsCount(pendingCount);
        }
      } catch (error) {
        console.error('Error fetching assessments:', error);
      } finally {
        setIsAssessmentLoading(false);
      }
    }

    getData();

    // Set up polling if assessment is not ready yet
    let pollingInterval: NodeJS.Timeout | null = null;

    if (!hasCompletedAssessment && pendingLearnerAssessmentsCount === 0) {
      pollingInterval = setInterval(() => {
        getData();
        setPollingCount(prev => prev + 1);

        // Stop polling after 10 attempts (about 100 seconds)
        if (pollingCount >= 10) {
          if (pollingInterval) clearInterval(pollingInterval);
        }
      }, 10000); // Poll every 10 seconds
    }

    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [actions, companyVariables.slug, user?.learnerSlug, hasCompletedAssessment, pollingCount]);

  return (
    <div className={styles.dashboardContainer}>
      {/* Welcome header section */}
      <div className={styles.dashboardHeader}>
        <h1>Welcome to your Kepler Dashboard</h1>
        <p>Your AI-powered skills and roles taxonomy is ready. Here's what you can do with your free account.</p>
      </div>

      {/* Assessment status section */}
      <div className={styles.assessmentSection}>
        {pendingLearnerAssessmentsCount > 0 ? (
          <ActionCard
            icon="/assessments-pending.svg"
            title={`Assessment${pendingLearnerAssessmentsCount === 1 ? '' : 's'} pending`}
            description={`You currently have ${pendingLearnerAssessmentsCount} assessment${pendingLearnerAssessmentsCount === 1 ? '' : 's'} pending. Complete your assessment to unlock more analytics and insights.`}
            action={{
              type: 'anchor',
              text: 'View Assessments',
              onClick: () => keplerNavigate(PagePath.yourAssessmentsBase),
            }}
          />
        ) : !hasCompletedAssessment ? (
          <WidgetCard
            title="Assessment is getting ready"
            subtitle={isAssessmentLoading ? `We're preparing your assessment...` : 'Your assessment will be ready soon. This may take a few minutes as our AI generates personalized content.'}
          />
        ) : null}
      </div>

      {/* Freemium features explanation */}
      <div className={styles.cardGrid}>
        <WidgetCard
          className={styles.infoCard}
          title="Your Free Account Features"
          subtitle="With your free account, you can:"
        >
          <div className={styles.infoCardContent}>
            <ul className={styles.featureList}>
              <li>Access your and manager your roles and Skills taxonomy</li>
              <li>Upload up to 5 team members</li>
              <li>Complete 1 free assessment</li>
              <li>View analytics after completing your assessment</li>
            </ul>
            <Button
              type="button"
              onClick={() => setIsPaywallModalOpen(true)}
              style={{ marginTop: '16px' }}
            >
              Upgrade for More Features
            </Button>
          </div>
        </WidgetCard>

        {/* People Management Card */}
        <FeatureCard
          title="People Management"
          description={`Manage up to ${maxFreemiumUsers} team members in your organization.`}
          stats={[
            { value: peopleCount, label: 'Added' },
            { value: maxFreemiumUsers - peopleCount, label: 'Remaining' }
          ]}
          actionLabel="Manage People"
          actionPath={PagePath.freemiumUserManagementBase}
          locked={false}
        />

        {/* Roles & Skills Card */}
        <FeatureCard
          title="Roles & Skills"
          description="Manage your AI-generated roles and skills taxonomy."
          stats={[
            { value: rolesCount, label: 'Roles' },
            { value: skillsCount, label: 'Skills' }
          ]}
          actionLabel="Manage Taxonomy"
          actionPath={PagePath.roleSkillManagementBase}
          locked={false}
        />
      </div>
      <div className={styles.assessmentSection}>
        <EntitiesCardWidget title="Your Roles & Skills Taxonomy" description="This taxonomy shows the relationship between roles and skills in your organization.">
          <DSMMatrix />
        </EntitiesCardWidget>
      </div>
      <div className={styles.assessmentSection}>
        <EntitiesCardWidget title="Insights" description="AI-generated insights based on your company data">
          <InsightsWidget
            insights={freemiumAccount?.insights || []}
            maxInsights={5}
            showUpgradePrompt={true}
          />
        </EntitiesCardWidget>
      </div>
      {/* Feature cards grid */}
      {/* Paywall Modal */}
      <PaywallModal
        isOpen={isPaywallModalOpen}
        onClose={() => setIsPaywallModalOpen(false)}
      />
    </div>
  );
}
